export enum UserRole {
  STAFF = 'staff',
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin',
}

export enum UserType {
  ANONYMOUS = 'anonymous',
  AUTHENTICATED = 'authenticated',
}

export enum PERMISSION {
  ADMIN = 'admin',
  META_WRITE = 'meta_write',
  GOOGLE_ADS_WRITE = 'google_ads_write',
  LINKEDIN_WRITE = 'linkedin_write',
  TIKTOK_WRITE = 'tiktok_write',
  REDDIT_WRITE = 'reddit_write',
  BING_ADS_WRITE = 'bing_ads_write',
  X_ADS_WRITE = 'x_ads_write',
  OPENAI_ADS_WRITE = 'openai_ads_write',
  VIEW = 'view',
}

export const PERMISSION_TITLE: Record<PERMISSION, string> = {
  [PERMISSION.ADMIN]: 'Admin',
  [PERMISSION.META_WRITE]: 'Meta Write Access',
  [PERMISSION.GOOGLE_ADS_WRITE]: 'Google Ads Write Access',
  [PERMISSION.LINKEDIN_WRITE]: 'LinkedIn Write Access',
  [PERMISSION.TIKTOK_WRITE]: 'TikTok Write Access',
  [PERMISSION.REDDIT_WRITE]: 'Reddit Write Access',
  [PERMISSION.BING_ADS_WRITE]: 'Bing Ads Write Access',
  [PERMISSION.X_ADS_WRITE]: 'X Ads Write Access',
  [PERMISSION.OPENAI_ADS_WRITE]: 'OpenAI Ads Write Access',
  [PERMISSION.VIEW]: 'Staff',
};

export const USER_ROLE_TITLE: Record<UserRole, string> = {
  [UserRole.ADMIN]: 'Admin',
  [UserRole.STAFF]: 'Staff',
  [UserRole.SUPER_ADMIN]: 'Super Admin',
};

// Role permissions are mutually exclusive (admin or staff)
export const ROLE_PERMISSIONS = [PERMISSION.ADMIN, PERMISSION.VIEW] as const;

export const ROLE_PERMISSION_OPTIONS = [
  {
    value: PERMISSION.ADMIN,
    label: 'Admin',
    description: 'Manage users, billing, and settings',
  },
  {
    value: PERMISSION.VIEW,
    label: 'Staff',
    description: 'Standard team member — interact with Leo and view data',
  },
];

// Platform permissions are independent toggles
export interface PlatformPermissionConfig {
  permission: PERMISSION;
  label: string;
  description: string;
}

export const PLATFORM_PERMISSIONS: PlatformPermissionConfig[] = [
  {
    permission: PERMISSION.META_WRITE,
    label: 'Meta Write Access',
    description:
      'Create, edit, and manage Meta campaigns, ad sets, and ads',
  },
  {
    permission: PERMISSION.GOOGLE_ADS_WRITE,
    label: 'Google Ads Write Access',
    description:
      'Create, edit, and manage Google Ads campaigns, ad groups, ads, and keywords',
  },
  {
    permission: PERMISSION.LINKEDIN_WRITE,
    label: 'LinkedIn Write Access',
    description:
      'Create, edit, and manage LinkedIn campaigns, creatives, and lead gen forms',
  },
  {
    permission: PERMISSION.TIKTOK_WRITE,
    label: 'TikTok Write Access',
    description:
      'Create, edit, and manage TikTok campaigns, ad groups, and ads',
  },
  {
    permission: PERMISSION.REDDIT_WRITE,
    label: 'Reddit Write Access',
    description:
      'Create, edit, and manage Reddit campaigns, ad groups, and ads',
  },
  {
    permission: PERMISSION.BING_ADS_WRITE,
    label: 'Bing Ads Write Access',
    description:
      'Create, edit, and manage Microsoft Advertising campaigns, ad groups, ads, and keywords',
  },
  {
    permission: PERMISSION.X_ADS_WRITE,
    label: 'X Ads Write Access',
    description:
      'Create, edit, and manage X (Twitter) Ads campaigns, line items, and promoted posts',
  },
  {
    permission: PERMISSION.OPENAI_ADS_WRITE,
    label: 'OpenAI Ads Write Access',
    description:
      'Create, edit, and manage OpenAI (ChatGPT) Ads campaigns, ad groups, and ads',
  },
];

// Legacy: all permissions as flat options (for backward compatibility)
export const PERMISSION_OPTIONS = (
  Object.keys(PERMISSION) as (keyof typeof PERMISSION)[]
).map((key) => {
  return {
    label: PERMISSION_TITLE[PERMISSION[key]],
    value: PERMISSION[key],
  };
});
